import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const safeText = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const crcTable = (() => {
  const table = [];
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
};

const escapeXml = (value) =>
  safeText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const writeUint16 = (bytes, value) => {
  bytes.push(value & 0xff, (value >>> 8) & 0xff);
};

const writeUint32 = (bytes, value) => {
  bytes.push(
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff
  );
};

const createStoredZip = (files) => {
  const encoder = new TextEncoder();
  const output = [];
  const centralDirectory = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = encoder.encode(file.content);
    const checksum = crc32(dataBytes);

    writeUint32(output, 0x04034b50);
    writeUint16(output, 20);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, checksum);
    writeUint32(output, dataBytes.length);
    writeUint32(output, dataBytes.length);
    writeUint16(output, nameBytes.length);
    writeUint16(output, 0);
    output.push(...nameBytes, ...dataBytes);

    centralDirectory.push({ file, nameBytes, dataBytes, checksum, offset });
    offset = output.length;
  });

  const centralStart = output.length;

  centralDirectory.forEach(({ nameBytes, dataBytes, checksum, offset: fileOffset }) => {
    writeUint32(output, 0x02014b50);
    writeUint16(output, 20);
    writeUint16(output, 20);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, checksum);
    writeUint32(output, dataBytes.length);
    writeUint32(output, dataBytes.length);
    writeUint16(output, nameBytes.length);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, 0);
    writeUint32(output, fileOffset);
    output.push(...nameBytes);
  });

  const centralSize = output.length - centralStart;

  writeUint32(output, 0x06054b50);
  writeUint16(output, 0);
  writeUint16(output, 0);
  writeUint16(output, files.length);
  writeUint16(output, files.length);
  writeUint32(output, centralSize);
  writeUint32(output, centralStart);
  writeUint16(output, 0);

  return new Blob([new Uint8Array(output)], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const buildMappedRows = (columns, rows) => {
  return rows.map((row, index) => {
    const item = { No: index + 1 };

    columns.forEach((col) => {
      item[col.label] = safeText(col.accessor(row));
    });

    return item;
  });
};

export const exportRowsToExcel = ({
  fileName,
  sheetName,
  columns,
  rows,
  title,
  subtitle,
  extraInfo = [],
}) => {
  const mappedRows = buildMappedRows(columns, rows);

  const worksheetData = [];

  if (title) {
    worksheetData.push([title]);
  }

  if (subtitle) {
    worksheetData.push([subtitle]);
  }

  if (extraInfo.length > 0) {
    extraInfo.forEach((item) => {
      worksheetData.push([item]);
    });
  }

  if (title || subtitle || extraInfo.length > 0) {
    worksheetData.push([]);
  }

  const headerRow = ['No', ...columns.map((c) => c.label)];
  worksheetData.push(headerRow);

  mappedRows.forEach((row) => {
    worksheetData.push(Object.values(row));
  });

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  const totalColumns = headerRow.length;
  const merges = [];

  if (title) {
    merges.push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: totalColumns - 1 },
    });
  }

  if (subtitle) {
    merges.push({
      s: { r: title ? 1 : 0, c: 0 },
      e: { r: title ? 1 : 0, c: totalColumns - 1 },
    });
  }

  worksheet['!merges'] = merges;

  worksheet['!cols'] = [
    { wch: 6 },
    ...columns.map((col) => {
      const label = col.label || '';
      if (label.toLowerCase().includes('dokumen')) return { wch: 28 };
      if (label.toLowerCase().includes('nama mata kuliah')) return { wch: 28 };
      if (label.toLowerCase().includes('unit')) return { wch: 24 };
      if (label.toLowerCase().includes('kompetensi')) return { wch: 20 };
      if (label.toLowerCase().includes('konversi')) return { wch: 18 };
      return { wch: Math.max(14, label.length + 2) };
    }),
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || 'Data');
  XLSX.writeFile(workbook, fileName || 'rekap.xlsx');
};

export const exportRowsToDocx = ({
  fileName,
  columns,
  rows,
  title,
  subtitle,
  extraInfo = [],
  layout = 'table',
  chapterNumber = '',
}) => {
  const run = (text, options = {}) => `
    <w:r>
      <w:rPr>
        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
        ${options.bold ? '<w:b/>' : ''}
        ${options.size ? `<w:sz w:val="${options.size}"/>` : ''}
      </w:rPr>
      <w:t xml:space="preserve">${escapeXml(text)}</w:t>
    </w:r>
  `;

  const paragraph = (text, options = {}) => `<w:p>${run(text, options)}</w:p>`;

  if (layout === 'paragraph') {
    const infoParagraphs = [title, subtitle, ...extraInfo]
      .filter(Boolean)
      .map((item, index) => paragraph(item, { bold: index === 0, size: index === 0 ? 32 : 24 }))
      .join('');

    const content = rows
      .map((row) =>
        columns
          .map(
            (col, index) => `
              ${paragraph(`${chapterNumber ? `${chapterNumber}.${index + 1} ` : ''}${col.label.toUpperCase()}`, {
                bold: true,
                size: 24,
              })}
              ${paragraph(col.accessor(row) || '-')}
            `
          )
          .join('')
      )
      .join('');

    const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${infoParagraphs}
    ${content}
    <w:sectPr><w:pgSz w:w="12240" w:h="15840"/></w:sectPr>
  </w:body>
</w:document>`;

    const blob = createStoredZip([
      {
        name: '[Content_Types].xml',
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
      },
      {
        name: '_rels/.rels',
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
      },
      {
        name: 'word/document.xml',
        content: documentXml,
      },
    ]);

    downloadBlob(blob, fileName || 'rekap.docx');
    return;
  }

  const bodyRows = rows
    .map((row, index) => {
      const cells = [
        `<w:tc><w:p><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t>${index + 1}</w:t></w:r></w:p></w:tc>`,
        ...columns.map(
          (col) =>
            `<w:tc><w:p><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t xml:space="preserve">${escapeXml(
              col.accessor(row)
            )}</w:t></w:r></w:p></w:tc>`
        ),
      ];
      return `<w:tr>${cells.join('')}</w:tr>`;
    })
    .join('');

  const headerCells = ['No', ...columns.map((col) => col.label)]
    .map(
      (label) =>
        `<w:tc><w:p><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/><w:b/></w:rPr><w:t>${escapeXml(label)}</w:t></w:r></w:p></w:tc>`
    )
    .join('');

  const infoParagraphs = [title, subtitle, ...extraInfo]
    .filter(Boolean)
    .map(
      (item) =>
        `<w:p><w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/></w:rPr><w:t xml:space="preserve">${escapeXml(item)}</w:t></w:r></w:p>`
    )
    .join('');

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${infoParagraphs}
    <w:tbl>
      <w:tr>${headerCells}</w:tr>
      ${bodyRows}
    </w:tbl>
    <w:sectPr><w:pgSz w:w="15840" w:h="12240" w:orient="landscape"/></w:sectPr>
  </w:body>
</w:document>`;

  const blob = createStoredZip([
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    },
    {
      name: 'word/document.xml',
      content: documentXml,
    },
  ]);

  downloadBlob(blob, fileName || 'rekap.docx');
};

export const exportCriteriaArchiveToDocx = ({
  fileName,
  title,
  subtitle,
  rows,
}) => {
  const criteriaOrder = [...new Set(rows.map((row) => row.criteriaCode))].sort(
    (a, b) =>
      Number(String(a).replace(/\D/g, '') || 0) -
      Number(String(b).replace(/\D/g, '') || 0)
  );
  const getChapterNumber = (criteriaCode) => {
    return Number(String(criteriaCode).replace(/\D/g, '') || 0) || criteriaCode;
  };

  const run = (text, options = {}) => `
    <w:r>
      <w:rPr>
        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
        ${options.bold ? '<w:b/>' : ''}
        ${options.size ? `<w:sz w:val="${options.size}"/>` : ''}
      </w:rPr>
      <w:t xml:space="preserve">${escapeXml(text)}</w:t>
    </w:r>
  `;

  const paragraph = (text, options = {}) => `
    <w:p>
      <w:pPr>
        <w:spacing w:after="${options.after || 160}" w:line="276" w:lineRule="auto"/>
        ${options.align ? `<w:jc w:val="${options.align}"/>` : ''}
      </w:pPr>
      ${run(text, options)}
    </w:p>
  `;

  const numberedParagraph = (text, level, options = {}) => `
    <w:p>
      <w:pPr>
        <w:numPr>
          <w:ilvl w:val="${level}"/>
          <w:numId w:val="${options.numId || 1}"/>
        </w:numPr>
        <w:spacing w:before="${options.before || 120}" w:after="${options.after || 120}" w:line="276" w:lineRule="auto"/>
      </w:pPr>
      ${run(text, options)}
    </w:p>
  `;

  const pageBreak = () => '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';

  const cell = (text, options = {}) => `
    <w:tc>
      <w:tcPr><w:tcW w:w="${options.width || 2400}" w:type="dxa"/></w:tcPr>
      ${paragraph(text, options)}
    </w:tc>
  `;

  const groupedRows = rows.reduce((groups, row) => {
    if (!groups[row.criteriaCode]) {
      groups[row.criteriaCode] = {
        title: row.criteriaTitle,
        fields: row.fields || [],
        rows: [],
      };
    }

    groups[row.criteriaCode].rows.push(row);
    return groups;
  }, {});

  const content = criteriaOrder
    .filter((criteriaCode) => groupedRows[criteriaCode])
    .map((criteriaCode, chapterIndex) => {
      const group = groupedRows[criteriaCode];
      const chapterNumber = getChapterNumber(criteriaCode);
      const numId = 100 + Number(chapterNumber || chapterIndex + 1);
      const chapterTitle = `${criteriaCode} ${group.title}`.toUpperCase();
      const chapterIntro = `${chapterIndex > 0 ? pageBreak() : ''}${numberedParagraph(chapterTitle, 0, {
        bold: true,
        size: 32,
        before: 0,
        after: 220,
        numId,
      })}`;

      const isTableCriteria =
        group.rows[0]?.criteriaType === 'tabel';

      if (!isTableCriteria) {
        return `
          ${chapterIntro}
          ${group.rows
            .map(
              (row) => `
                ${paragraph(`Program Studi: ${row.prodiText}`, { bold: true, size: 22, after: 180 })}
                ${group.fields
                  .map(
                    (field) => `
                      ${numberedParagraph(field.label.toUpperCase(), 1, {
                        bold: true,
                        size: 24,
                        before: 180,
                        after: 100,
                        numId,
                      })}
                      ${paragraph(row.rawData[field.name] || row.rawData.data_json?.[field.name] || '-', {
                        size: 24,
                        after: 180,
                      })}
                    `
                  )
                  .join('')}
              `
            )
            .join('')}
        `;
      }

      const headerCells = [
        cell('No', { bold: true, width: 700 }),
        cell('Prodi', { bold: true, width: 2200 }),
        ...group.fields.map((field) => cell(field.label, { bold: true, width: 2600 })),
      ].join('');

      const bodyRows = group.rows
        .map((row, rowIndex) => {
          const cells = [
            cell(rowIndex + 1, { width: 700 }),
            cell(row.prodiText, { width: 2200 }),
            ...group.fields.map((field) =>
              cell(row.rawData[field.name] || row.rawData.data_json?.[field.name], { width: 2600 })
            ),
          ].join('');

          return `<w:tr>${cells}</w:tr>`;
        })
        .join('');

      return `
        ${chapterIntro}
        <w:tbl>
          <w:tblPr>
            <w:tblBorders>
              <w:top w:val="single" w:sz="4" w:space="0" w:color="000000"/>
              <w:left w:val="single" w:sz="4" w:space="0" w:color="000000"/>
              <w:bottom w:val="single" w:sz="4" w:space="0" w:color="000000"/>
              <w:right w:val="single" w:sz="4" w:space="0" w:color="000000"/>
              <w:insideH w:val="single" w:sz="4" w:space="0" w:color="000000"/>
              <w:insideV w:val="single" w:sz="4" w:space="0" w:color="000000"/>
            </w:tblBorders>
          </w:tblPr>
          <w:tr>${headerCells}</w:tr>
          ${bodyRows}
        </w:tbl>
      `;
    })
    .join('');

  const infoParagraphs = [title, subtitle]
    .filter(Boolean)
    .map(
      (item) =>
        paragraph(item, { bold: true, size: 28, align: 'center', after: 180 })
    )
    .join('');

  const numberingOverrides = criteriaOrder
    .map((criteriaCode, index) => {
      const chapterNumber = Number(getChapterNumber(criteriaCode) || index + 1);
      return `
        <w:num w:numId="${100 + chapterNumber}">
          <w:abstractNumId w:val="1"/>
          <w:lvlOverride w:ilvl="0">
            <w:startOverride w:val="${chapterNumber}"/>
          </w:lvlOverride>
        </w:num>
      `;
    })
    .join('');

  const numberingXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:numbering xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:abstractNum w:abstractNumId="1">
    <w:multiLevelType w:val="multilevel"/>
    <w:lvl w:ilvl="0">
      <w:start w:val="1"/>
      <w:numFmt w:val="decimal"/>
      <w:lvlText w:val="BAB %1 -"/>
      <w:lvlJc w:val="left"/>
      <w:pPr>
        <w:ind w:left="0" w:hanging="0"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
        <w:b/>
        <w:sz w:val="32"/>
      </w:rPr>
    </w:lvl>
    <w:lvl w:ilvl="1">
      <w:start w:val="1"/>
      <w:numFmt w:val="decimal"/>
      <w:lvlText w:val="%1.%2"/>
      <w:lvlJc w:val="left"/>
      <w:pPr>
        <w:ind w:left="0" w:hanging="0"/>
      </w:pPr>
      <w:rPr>
        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
        <w:b/>
        <w:sz w:val="24"/>
      </w:rPr>
    </w:lvl>
  </w:abstractNum>
  ${numberingOverrides}
</w:numbering>`;

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${infoParagraphs}
    ${infoParagraphs ? pageBreak() : ''}
    ${content}
    <w:sectPr><w:pgSz w:w="15840" w:h="12240" w:orient="landscape"/></w:sectPr>
  </w:body>
</w:document>`;

  const blob = createStoredZip([
    {
      name: '[Content_Types].xml',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/numbering.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"/>
</Types>`,
    },
    {
      name: '_rels/.rels',
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`,
    },
      {
        name: 'word/document.xml',
        content: documentXml,
      },
      {
        name: 'word/_rels/document.xml.rels',
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rIdNumbering" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering" Target="numbering.xml"/>
</Relationships>`,
      },
      {
        name: 'word/numbering.xml',
        content: numberingXml,
      },
    ]);

  downloadBlob(blob, fileName || 'dokumen-akreditasi.docx');
};

export const exportRowsToPdf = ({
  title,
  fileName,
  columns,
  rows,
  campusName = 'Politeknik Negeri Manado',
  systemName = 'Sistem Informasi Akreditasi',
  infoLeft = [],
  infoRight = [],
}) => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(campusName, 14, 14);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(systemName, 14, 20);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title || 'Rekap Data', 14, 30);

  let startY = 38;

  if (infoLeft.length > 0 || infoRight.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    infoLeft.forEach((item, index) => {
      doc.text(item, 14, startY + index * 5);
    });

    infoRight.forEach((item, index) => {
      doc.text(item, 170, startY + index * 5);
    });

    startY += Math.max(infoLeft.length, infoRight.length) * 5 + 4;
  }

  const head = [['No', ...columns.map((c) => c.label)]];
  const body = rows.map((row, index) => [
    index + 1,
    ...columns.map((col) => safeText(col.accessor(row))),
  ]);

  autoTable(doc, {
    startY,
    head,
    body,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [11, 87, 208],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    bodyStyles: {
      textColor: [33, 37, 41],
    },
    theme: 'grid',
    margin: { left: 14, right: 14 },
  });

  doc.save(fileName || 'rekap.pdf');
};
